<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from 'chart.js';
import { Bar } from 'vue-chartjs';
import aiDashboardAPI from 'dashboard/api/aiDashboard';

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

// ── Date range (default 7 days) ─────────────────────────────────────────────
const today = new Date();
const defaultFrom = new Date(today);
defaultFrom.setDate(today.getDate() - 6);

const fromDate = ref(defaultFrom.toISOString().slice(0, 10));
const toDate = ref(today.toISOString().slice(0, 10));

// ── State ────────────────────────────────────────────────────────────────────
const loading = ref(false);
const error = ref(null);
const data = ref(null);

// ── Fetch ────────────────────────────────────────────────────────────────────
async function fetchDashboard() {
  loading.value = true;
  error.value = null;
  try {
    const res = await aiDashboardAPI.get({
      from: fromDate.value,
      to: toDate.value,
    });
    data.value = res.data;
  } catch (e) {
    error.value =
      e?.response?.data?.error || e.message || 'Erro ao carregar dados';
  } finally {
    loading.value = false;
  }
}

onMounted(fetchDashboard);
watch([fromDate, toDate], fetchDashboard);

// ── Helpers ──────────────────────────────────────────────────────────────────
function formatSeconds(secs) {
  if (!secs || secs <= 0) return '—';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}min`;
  if (m > 0) return `${m}min ${s}s`;
  return `${s}s`;
}

// ── KPI cards ────────────────────────────────────────────────────────────────
const kpiRow1 = computed(() => {
  if (!data.value) return [];
  const d = data.value;
  return [
    {
      label: 'Total de Conversas',
      value: d.total_conversations,
      sub: 'conversas criadas no período',
      icon: 'i-lucide-message-square',
      accent: 'blue',
    },
    {
      label: 'Transbordados',
      value: d.handoff_count,
      sub: `${d.handoff_rate}% taxa de transbordo`,
      icon: 'i-lucide-arrow-right-left',
      accent: 'amber',
    },
    {
      label: 'Abandono após IA',
      value: d.abandonment_after_ai,
      sub: 'cliente não respondeu a IA há 2+ dias',
      icon: 'i-lucide-bot-off',
      accent: 'red',
    },
    {
      label: 'Abandono após Consultor',
      value: d.abandonment_after_consultant,
      sub: 'cliente não respondeu o consultor há 2+ dias',
      icon: 'i-lucide-user-x',
      accent: 'orange',
    },
    {
      label: 'Tempo Médio de Transbordo',
      value: formatSeconds(d.avg_handoff_seconds),
      sub: 'do 1º contato ao transbordo',
      icon: 'i-lucide-timer',
      accent: 'purple',
    },
  ];
});

const kpiRow2 = computed(() => {
  if (!data.value) return [];
  const d = data.value;
  return [
    {
      label: 'Concluídos por Consultores',
      value: d.human_resolved_count,
      sub: 'resolvidos por agentes humanos',
      icon: 'i-lucide-user-check',
      accent: 'indigo',
    },
    {
      label: 'Concluídos por IA',
      value: d.ai_resolved_count,
      sub: `${d.ai_resolution_rate}% taxa de conclusão IA`,
      icon: 'i-lucide-bot',
      accent: 'emerald',
    },
    {
      label: 'Encerramentos Forçados',
      value: d.forced_closure_count,
      sub: '5 dias sem atividade',
      icon: 'i-lucide-timer-off',
      accent: 'rose',
    },
    {
      label: 'Taxa de Conclusão por IA',
      value: `${d.ai_resolution_rate}%`,
      sub: 'IA resolveu / total resolvido',
      icon: 'i-lucide-sparkles',
      accent: 'teal',
    },
    {
      label: 'Tempo de Conclusão IA',
      value: formatSeconds(d.ai_avg_resolution_seconds),
      sub: 'mediana do atendimento pela IA',
      icon: 'i-lucide-zap',
      accent: 'cyan',
    },
    {
      label: 'Tempo Real da Conversa',
      value: formatSeconds(d.real_conversation_median_seconds),
      sub: 'mediana entre 1ª e última mensagem',
      icon: 'i-lucide-clock',
      accent: 'slate',
    },
  ];
});

const accentClasses = {
  blue: {
    iconBg: 'bg-blue-100 dark:bg-blue-500/15',
    iconColor: 'text-blue-600 dark:text-blue-400',
    bar: 'bg-blue-500',
  },
  amber: {
    iconBg: 'bg-amber-100 dark:bg-amber-500/15',
    iconColor: 'text-amber-600 dark:text-amber-400',
    bar: 'bg-amber-500',
  },
  red: {
    iconBg: 'bg-red-100 dark:bg-red-500/15',
    iconColor: 'text-red-600 dark:text-red-400',
    bar: 'bg-red-500',
  },
  orange: {
    iconBg: 'bg-orange-100 dark:bg-orange-500/15',
    iconColor: 'text-orange-600 dark:text-orange-400',
    bar: 'bg-orange-500',
  },
  purple: {
    iconBg: 'bg-purple-100 dark:bg-purple-500/15',
    iconColor: 'text-purple-600 dark:text-purple-400',
    bar: 'bg-purple-500',
  },
  indigo: {
    iconBg: 'bg-indigo-100 dark:bg-indigo-500/15',
    iconColor: 'text-indigo-600 dark:text-indigo-400',
    bar: 'bg-indigo-500',
  },
  emerald: {
    iconBg: 'bg-emerald-100 dark:bg-emerald-500/15',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    bar: 'bg-emerald-500',
  },
  rose: {
    iconBg: 'bg-rose-100 dark:bg-rose-500/15',
    iconColor: 'text-rose-600 dark:text-rose-400',
    bar: 'bg-rose-500',
  },
  teal: {
    iconBg: 'bg-teal-100 dark:bg-teal-500/15',
    iconColor: 'text-teal-600 dark:text-teal-400',
    bar: 'bg-teal-500',
  },
  cyan: {
    iconBg: 'bg-cyan-100 dark:bg-cyan-500/15',
    iconColor: 'text-cyan-600 dark:text-cyan-400',
    bar: 'bg-cyan-500',
  },
  slate: {
    iconBg: 'bg-slate-100 dark:bg-slate-500/15',
    iconColor: 'text-slate-600 dark:text-slate-400',
    bar: 'bg-slate-500',
  },
};

function getAccent(name) {
  return accentClasses[name] || accentClasses.slate;
}

// ── Business hours badges ────────────────────────────────────────────────────
const businessHoursCount = computed(
  () => data.value?.business_hours_count ?? 0
);
const offHoursCount = computed(() => data.value?.off_hours_count ?? 0);
const totalForBadges = computed(
  () => businessHoursCount.value + offHoursCount.value || 1
);
const businessPct = computed(() =>
  ((businessHoursCount.value / totalForBadges.value) * 100).toFixed(1)
);
const offPct = computed(() =>
  ((offHoursCount.value / totalForBadges.value) * 100).toFixed(1)
);

// ── Team funnel ──────────────────────────────────────────────────────────────
const teamFunnel = computed(() => data.value?.team_funnel ?? []);
const maxTeamCount = computed(
  () => teamFunnel.value.reduce((m, t) => Math.max(m, t.count), 0) || 1
);
const totalHandoffs = computed(() => data.value?.handoff_count || 1);

// ── Chart data ───────────────────────────────────────────────────────────────
const dowLabels = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const isDark = computed(() =>
  document.documentElement.classList.contains('dark')
);

const conversationsAndHandoffsChart = computed(() => ({
  labels: dowLabels,
  datasets: [
    {
      label: 'Atendimentos',
      data: data.value?.dow_conversations || Array(7).fill(0),
      backgroundColor: 'rgba(59, 130, 246, 0.8)',
      hoverBackgroundColor: 'rgba(59, 130, 246, 1)',
      borderRadius: 6,
      borderSkipped: false,
    },
    {
      label: 'Transbordos',
      data: data.value?.dow_handoffs || Array(7).fill(0),
      backgroundColor: 'rgba(245, 158, 11, 0.8)',
      hoverBackgroundColor: 'rgba(245, 158, 11, 1)',
      borderRadius: 6,
      borderSkipped: false,
    },
  ],
}));

const aiVsHumanChart = computed(() => ({
  labels: dowLabels,
  datasets: [
    {
      label: 'Concluídos por IA',
      data: data.value?.dow_ai_resolved || Array(7).fill(0),
      backgroundColor: 'rgba(16, 185, 129, 0.8)',
      hoverBackgroundColor: 'rgba(16, 185, 129, 1)',
      borderRadius: 6,
      borderSkipped: false,
    },
    {
      label: 'Concluídos por Consultores',
      data: data.value?.dow_human_resolved || Array(7).fill(0),
      backgroundColor: 'rgba(99, 102, 241, 0.8)',
      hoverBackgroundColor: 'rgba(99, 102, 241, 1)',
      borderRadius: 6,
      borderSkipped: false,
    },
  ],
}));

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { intersect: false, mode: 'index' },
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 20,
        font: { size: 12, weight: '500' },
      },
    },
    tooltip: {
      backgroundColor: isDark.value
        ? 'rgba(30, 41, 59, 0.95)'
        : 'rgba(15, 23, 42, 0.9)',
      titleFont: { size: 13, weight: '600' },
      bodyFont: { size: 12 },
      padding: 12,
      cornerRadius: 8,
      boxPadding: 4,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        precision: 0,
        font: { size: 11 },
        color: isDark.value ? 'rgba(148,163,184,0.7)' : 'rgba(100,116,139,0.7)',
      },
      grid: {
        color: isDark.value ? 'rgba(148,163,184,0.08)' : 'rgba(0,0,0,0.04)',
      },
      border: { display: false },
    },
    x: {
      ticks: {
        font: { size: 11, weight: '500' },
        color: isDark.value ? 'rgba(148,163,184,0.7)' : 'rgba(100,116,139,0.7)',
      },
      grid: { display: false },
      border: { display: false },
    },
  },
}));

// ── Consultant KPIs ──────────────────────────────────────────────────────────
const consultantKpis = computed(() => data.value?.consultant_kpis ?? []);
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <!-- Header -->
    <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-2xl font-bold tracking-tight text-n-slate-12">
          Dashboard IA
        </h1>
        <p class="mt-1 text-sm text-n-slate-10">
          Métricas de performance da IA, transbordos e consultores
        </p>
      </div>
      <div
        class="flex items-center gap-2.5 rounded-xl border border-n-weak bg-white px-4 py-2.5 shadow-sm dark:bg-n-alpha-2"
      >
        <span class="i-lucide-calendar-days h-4 w-4 text-n-slate-10" />
        <input
          v-model="fromDate"
          type="date"
          class="w-[130px] bg-transparent text-sm font-medium text-n-slate-12 outline-none"
        />
        <span class="text-n-slate-8">→</span>
        <input
          v-model="toDate"
          type="date"
          class="w-[130px] bg-transparent text-sm font-medium text-n-slate-12 outline-none"
        />
      </div>
    </div>

    <!-- Loading skeleton -->
    <div v-if="loading" class="flex flex-col gap-4">
      <div class="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <div
          v-for="i in 5"
          :key="i"
          class="h-28 animate-pulse rounded-xl bg-n-alpha-3"
        />
      </div>
      <div class="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <div
          v-for="i in 6"
          :key="i"
          class="h-28 animate-pulse rounded-xl bg-n-alpha-3"
        />
      </div>
    </div>

    <!-- Error -->
    <div
      v-else-if="error"
      class="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800/40 dark:bg-red-900/20 dark:text-red-400"
    >
      <span class="i-lucide-alert-circle h-5 w-5 shrink-0" />
      <span class="text-sm">{{ error }}</span>
      <button
        class="ml-auto rounded-lg px-3 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30"
        @click="fetchDashboard"
      >
        Tentar novamente
      </button>
    </div>

    <template v-else-if="data">
      <!-- KPI Row 1 (5 cards) -->
      <div class="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <div
          v-for="kpi in kpiRow1"
          :key="kpi.label"
          class="group relative flex flex-col justify-between overflow-hidden rounded-xl border border-n-weak bg-white p-4 transition-shadow hover:shadow-md dark:bg-n-alpha-2 dark:hover:bg-n-alpha-3"
        >
          <div
            :class="[getAccent(kpi.accent).bar, 'absolute left-0 top-0 h-1 w-full opacity-60']"
          />
          <div class="flex items-start justify-between">
            <p class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10">
              {{ kpi.label }}
            </p>
            <div
              :class="[
                getAccent(kpi.accent).iconBg,
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
              ]"
            >
              <span
                :class="[kpi.icon, 'h-4 w-4', getAccent(kpi.accent).iconColor]"
              />
            </div>
          </div>
          <div class="mt-3">
            <p class="text-2xl font-bold tabular-nums text-n-slate-12">
              {{ kpi.value }}
            </p>
            <p class="mt-1 text-[11px] leading-tight text-n-slate-9">
              {{ kpi.sub }}
            </p>
          </div>
        </div>
      </div>

      <!-- KPI Row 2 (6 cards) -->
      <div class="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <div
          v-for="kpi in kpiRow2"
          :key="kpi.label"
          class="group relative flex flex-col justify-between overflow-hidden rounded-xl border border-n-weak bg-white p-4 transition-shadow hover:shadow-md dark:bg-n-alpha-2 dark:hover:bg-n-alpha-3"
        >
          <div
            :class="[getAccent(kpi.accent).bar, 'absolute left-0 top-0 h-1 w-full opacity-60']"
          />
          <div class="flex items-start justify-between">
            <p class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10">
              {{ kpi.label }}
            </p>
            <div
              :class="[
                getAccent(kpi.accent).iconBg,
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
              ]"
            >
              <span
                :class="[kpi.icon, 'h-4 w-4', getAccent(kpi.accent).iconColor]"
              />
            </div>
          </div>
          <div class="mt-3">
            <p class="text-2xl font-bold tabular-nums text-n-slate-12">
              {{ kpi.value }}
            </p>
            <p class="mt-1 text-[11px] leading-tight text-n-slate-9">
              {{ kpi.sub }}
            </p>
          </div>
        </div>
      </div>

      <!-- Business Hours Strip -->
      <div
        class="flex flex-wrap items-center gap-3 rounded-xl border border-n-weak bg-white p-4 dark:bg-n-alpha-2"
      >
        <div class="flex items-center gap-3">
          <div class="flex items-center gap-2">
            <div
              class="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-500/15"
            >
              <span class="i-lucide-sun h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <span class="text-sm font-medium text-n-slate-11">
              Horário Comercial
            </span>
          </div>
          <div
            class="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-bold tabular-nums text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400"
          >
            {{ businessHoursCount }}
            <span class="font-normal text-emerald-600/70 dark:text-emerald-400/60">
              ({{ businessPct }}%)
            </span>
          </div>
        </div>

        <div
          class="h-5 w-px bg-n-alpha-3"
        />

        <div class="flex items-center gap-3">
          <div class="flex items-center gap-2">
            <div
              class="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-500/15"
            >
              <span class="i-lucide-moon h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <span class="text-sm font-medium text-n-slate-11">
              Fora do Horário
            </span>
          </div>
          <div
            class="rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-bold tabular-nums text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-400"
          >
            {{ offHoursCount }}
            <span class="font-normal text-indigo-600/70 dark:text-indigo-400/60">
              ({{ offPct }}%)
            </span>
          </div>
        </div>

        <div
          class="h-5 w-px bg-n-alpha-3"
        />

        <span class="text-[11px] text-n-slate-9">
          Seg–Sex 9h às 18h (horário de Brasília)
        </span>
      </div>

      <!-- Team Funnel -->
      <div
        class="rounded-xl border border-n-weak bg-white p-5 dark:bg-n-alpha-2"
      >
        <h2
          class="flex items-center gap-2 text-sm font-semibold text-n-slate-12"
        >
          <span
            class="flex h-7 w-7 items-center justify-center rounded-lg bg-n-brand/10"
          >
            <span class="i-lucide-git-branch h-3.5 w-3.5 text-n-brand" />
          </span>
          Funil de Conversão — Transbordos por Time
        </h2>

        <div
          v-if="teamFunnel.length === 0"
          class="py-10 text-center text-sm text-n-slate-9"
        >
          Nenhum transbordo por time registrado no período.
        </div>

        <div v-else class="mt-4 flex flex-col gap-3.5">
          <div
            v-for="item in teamFunnel"
            :key="item.team"
            class="group"
          >
            <div class="mb-1.5 flex items-center justify-between text-xs">
              <span class="font-medium text-n-slate-12">
                {{ item.team || 'Sem time' }}
              </span>
              <div class="flex items-center gap-2">
                <span class="font-bold tabular-nums text-n-slate-12">
                  {{ item.count }}
                </span>
                <span
                  class="rounded bg-n-alpha-3 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-n-slate-10"
                >
                  {{ ((item.count / totalHandoffs) * 100).toFixed(1) }}%
                </span>
              </div>
            </div>
            <div
              class="h-2 w-full overflow-hidden rounded-full bg-n-alpha-3"
            >
              <div
                class="h-2 rounded-full bg-n-brand transition-all duration-700 ease-out"
                :style="{ width: `${(item.count / maxTeamCount) * 100}%` }"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Row -->
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div
          class="rounded-xl border border-n-weak bg-white p-5 dark:bg-n-alpha-2"
        >
          <h2
            class="flex items-center gap-2 text-sm font-semibold text-n-slate-12"
          >
            <span
              class="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-500/15"
            >
              <span class="i-lucide-bar-chart-3 h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
            </span>
            Atendimentos e Transbordos
          </h2>
          <p class="mb-4 mt-0.5 text-[11px] text-n-slate-9">
            Distribuição por dia da semana
          </p>
          <div class="h-64">
            <Bar
              :data="conversationsAndHandoffsChart"
              :options="chartOptions"
            />
          </div>
        </div>

        <div
          class="rounded-xl border border-n-weak bg-white p-5 dark:bg-n-alpha-2"
        >
          <h2
            class="flex items-center gap-2 text-sm font-semibold text-n-slate-12"
          >
            <span
              class="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-500/15"
            >
              <span class="i-lucide-bar-chart-3 h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
            </span>
            Concluídos por IA vs Consultores
          </h2>
          <p class="mb-4 mt-0.5 text-[11px] text-n-slate-9">
            Distribuição por dia da semana
          </p>
          <div class="h-64">
            <Bar :data="aiVsHumanChart" :options="chartOptions" />
          </div>
        </div>
      </div>

      <!-- Consultant KPIs Table -->
      <div
        class="rounded-xl border border-n-weak bg-white p-5 dark:bg-n-alpha-2"
      >
        <h2
          class="flex items-center gap-2 text-sm font-semibold text-n-slate-12"
        >
          <span
            class="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-500/15"
          >
            <span class="i-lucide-users h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
          </span>
          KPIs dos Consultores
        </h2>

        <div
          v-if="consultantKpis.length === 0"
          class="py-10 text-center text-sm text-n-slate-9"
        >
          Nenhum consultor com atendimentos resolvidos no período.
        </div>

        <div v-else class="mt-4 overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-n-weak">
                <th
                  class="pb-3 pr-4 text-left text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
                >
                  Agente
                </th>
                <th
                  class="pb-3 pr-4 text-right text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
                >
                  Conversas
                </th>
                <th
                  class="pb-3 pr-4 text-right text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
                >
                  Tempo Médio 1ª Resposta
                </th>
                <th
                  class="pb-3 text-right text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
                >
                  Tempo Real da Conversa
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="agent in consultantKpis"
                :key="agent.id"
                class="border-b border-n-weak/50 transition-colors last:border-0 hover:bg-n-alpha-1"
              >
                <td class="py-3.5 pr-4">
                  <div class="flex items-center gap-2.5">
                    <div
                      class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-400"
                    >
                      {{ agent.name.charAt(0) }}
                    </div>
                    <span class="font-medium text-n-slate-12">
                      {{ agent.name }}
                    </span>
                  </div>
                </td>
                <td
                  class="py-3.5 pr-4 text-right font-bold tabular-nums text-n-slate-12"
                >
                  {{ agent.conversations_count }}
                </td>
                <td
                  class="py-3.5 pr-4 text-right tabular-nums text-n-slate-11"
                >
                  {{ formatSeconds(agent.avg_first_response_seconds) }}
                </td>
                <td class="py-3.5 text-right tabular-nums text-n-slate-11">
                  {{ formatSeconds(agent.avg_conversation_seconds) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>
